/**
 * GA4 + product analytics — consent-gated.
 *
 * No script tag is loaded until the user has explicitly granted analytics
 * consent (via the CookieBanner component). Once granted we:
 *   1. inject the gtag script with the configured measurement ID
 *   2. send a page_view for the current pathname
 *   3. wire `analytics.event()` so callers can fire events without knowing
 *      whether GA is loaded yet (events queued before consent are dropped —
 *      we don't want to retroactively send signups the user hasn't agreed to)
 *
 * # Event taxonomy
 *
 * We fire two flavors of events:
 *
 *   - **Custom events** (`scan_run`, `plan_upgraded`, `support_ticket_submitted`,
 *     `cta_clicked`, `domain_added`, `first_domain_added`, `onboarding_completed`,
 *     `api_key_created`, `alert_resolved`) — internally meaningful product
 *     verbs. Use these for product reports.
 *
 *   - **GA4-recommended events** (`sign_up`, `login`, `begin_checkout`,
 *     `purchase`) — Google's canonical names. GA4 automatically wires these
 *     into Acquisition / Monetization reports and the conversion-funnel UI.
 *     Always pass `value` + `currency` to `purchase` so revenue reports plot.
 *
 * Both flavors fire in parallel where they overlap (e.g. signup_completed +
 * sign_up). That's deliberate — keeps existing dashboards working while the
 * GA4-native reports light up too. GA4 doesn't charge per event name.
 */
const STORAGE_KEY = 'ig:cookie-consent';
const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;

type ConsentValue = 'granted' | 'denied' | null;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let scriptInjected = false;
let currentUserId: string | null = null;

/**
 * Plan list-price in USD/mo. Used as `value` on begin_checkout / purchase.
 * Keep in sync with PricingPage TIERS / api/src/lib/entitlements.ts.
 */
const PLAN_USD_MONTHLY: Record<string, number> = {
  free: 0,
  entry: 29,
  pro: 99,
  agency: 299,
};

export function getConsent(): ConsentValue {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === 'granted' || v === 'denied') return v;
  return null;
}

export function setConsent(v: 'granted' | 'denied') {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, v);
  if (v === 'granted') ensureLoaded();
  // We don't try to "unload" GA on denial — denial just means we never injected
  // it in the first place. If consent flips after a granted session the page
  // reload clears the dataLayer.
}

function ensureLoaded() {
  if (scriptInjected) return;
  if (!MEASUREMENT_ID) return;
  if (typeof document === 'undefined') return;

  // Set up the dataLayer + gtag stub BEFORE injecting gtag.js. Critical: use
  // the canonical `arguments`-based stub (NOT `(...args)`). gtag.js inspects
  // the existing stub to decide whether to take over, and a spread-args
  // variant fails that check — gtag.js loads, but never actually fires
  // /g/collect requests, leaving Realtime empty. Symptom we hit: window.gtag
  // stays as the 40-char stub even after gtag.js fetches with a 200.
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, prefer-rest-params
  window.gtag = function gtag() {
    // Use Arguments, not a real Array — this is the canonical pattern gtag.js
    // expects.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments as unknown as unknown[]);
  };

  // Google Consent Mode v2 — without an explicit consent signal GA4 treats
  // every flag as "implicit" and silently drops `/g/collect` requests (you
  // see gtag.js load 200 OK but Realtime stays empty). We only call
  // ensureLoaded() AFTER the user has clicked Accept on the cookie banner,
  // so granting analytics_storage is correct. We keep ads-related flags
  // denied — we don't run ads and don't want to imply consent for them.
  // Must be pushed BEFORE the config call.
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('js', new Date());
  // anonymize_ip is honored even on GA4 — pairs with Privacy policy claim
  // about "IP-truncated analytics".
  // user_id is set here if we already know the auth state (e.g. user
  // accepted the banner mid-session); otherwise identify() will set it later.
  window.gtag('config', MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: true,
    ...(currentUserId ? { user_id: currentUserId } : {}),
  });

  // Now inject gtag.js — it'll process the queued dataLayer entries.
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);

  scriptInjected = true;
}

/**
 * Fire a custom event. No-op if consent isn't granted or no measurement id is
 * configured. For the GA4-recommended events use the dedicated helpers below
 * so revenue + funnel reports light up automatically.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}) {
  if (getConsent() !== 'granted') return;
  if (!MEASUREMENT_ID) return;
  ensureLoaded();
  window.gtag?.('event', name, params);
}

/**
 * Page view. Called from a router-listening hook so SPAs hit GA on every
 * navigation, not just initial load.
 */
export function trackPageView(path: string) {
  if (getConsent() !== 'granted') return;
  if (!MEASUREMENT_ID) return;
  ensureLoaded();
  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_location: window.location.origin + path,
  });
}

/**
 * Tie subsequent events to a user. Called from AuthProvider whenever auth
 * resolves to a real user. Setting `user_id` lets GA4 stitch sessions across
 * devices and powers cohort + retention reports.
 *
 * Safe to call before consent — we cache the id and apply it on first
 * ensureLoaded(). Safe to call repeatedly with the same id.
 */
export function identify(userId: string, traits: Record<string, unknown> = {}) {
  currentUserId = userId;
  if (getConsent() !== 'granted') return;
  if (!MEASUREMENT_ID) return;
  ensureLoaded();
  // `set` applies to all subsequent events on this gtag instance.
  window.gtag?.('set', { user_id: userId, ...traits });
  // Also reconfig so the property's own user_id field gets updated.
  window.gtag?.('config', MEASUREMENT_ID, {
    user_id: userId,
    anonymize_ip: true,
    send_page_view: false, // page_view is fired separately by the router hook
  });
}

/** Clear user_id — call from signOut. */
export function revoke() {
  currentUserId = null;
  if (getConsent() !== 'granted') return;
  if (!MEASUREMENT_ID) return;
  window.gtag?.('set', { user_id: null });
}

// ---------------------------------------------------------------------------
// GA4-recommended events (mirror our internal events so reports work both ways)
// ---------------------------------------------------------------------------

/** Fired after the user confirms their email and the account exists. */
export function trackSignUp(method: 'email' = 'email') {
  trackEvent('sign_up', { method });
  trackEvent('signup_completed', { method });
}

/** Fired after a returning user signs in successfully. */
export function trackLogin(method: 'email' = 'email') {
  trackEvent('login', { method });
}

/** Fired when the user lands on the checkout page with a valid plan param. */
export function trackBeginCheckout(plan: string) {
  const value = PLAN_USD_MONTHLY[plan] ?? 0;
  trackEvent('begin_checkout', {
    currency: 'USD',
    value,
    items: [{ item_id: plan, item_name: `InboxGuard ${plan}`, price: value, quantity: 1 }],
  });
}

/**
 * Fired when a subscription is created. `kind: 'trial'` is a 14-day no-card
 * activation (zero revenue today, but the conversion event matters for
 * cohorts); `kind: 'paid'` is post-Stripe-success.
 *
 * Always fires both `purchase` (GA4 recommended → Monetization reports) and
 * `plan_upgraded` (our custom event → existing dashboards).
 */
export function trackPurchase(opts: {
  plan: string;
  kind: 'trial' | 'paid';
  transactionId?: string;
}) {
  const { plan, kind, transactionId } = opts;
  const value = kind === 'paid' ? (PLAN_USD_MONTHLY[plan] ?? 0) : 0;
  trackEvent('purchase', {
    transaction_id: transactionId ?? `${kind}_${plan}_${Date.now()}`,
    currency: 'USD',
    value,
    items: [{ item_id: plan, item_name: `InboxGuard ${plan}`, price: value, quantity: 1 }],
    payment_type: kind,
  });
  trackEvent('plan_upgraded', { plan, type: kind });
}

/**
 * Generic CTA click. `label` should be a stable identifier, not free text
 * (so reports group cleanly). `surface` is the page area: 'hero', 'pricing',
 * 'footer', 'header', etc.
 */
export function trackCtaClick(label: string, surface: string, extra: Record<string, unknown> = {}) {
  trackEvent('cta_clicked', { cta_label: label, cta_surface: surface, ...extra });
}

export const analytics = {
  trackEvent,
  trackPageView,
  identify,
  revoke,
  trackSignUp,
  trackLogin,
  trackBeginCheckout,
  trackPurchase,
  trackCtaClick,
  getConsent,
  setConsent,
};
